import { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Container,
    Grid,
    Snackbar,
    Alert,
    Stack,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import {
    Send as SendIcon,
    Cancel as CancelIcon,
    AttachFile as AttachFileIcon,
    Delete as DeleteIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import { getClientPrincipal } from '../services/auth';
import {
    submitBusinessCase,
    saveDraftBusinessCase,
    fetchDraftBusinessCase,
    deleteDraftBusinessCase,
    BusinessCaseClient
} from '../services/businessCase';
import { sendBusinessCaseSubmission } from '../services/email';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface BusinessCaseFormData {
    // Header fields
    opportunity_name: string;
    client: string;
    proposal_coordinator: string;
    country_region: string;
    duration_of_project: string;
    budget: string;
    deadline: string;
    workstream: string;
    
    // Question fields
    strategic_case: string;
    competitive_advantage: string;
    corporate_experience: string;
    prospective_team_configuration: string;
    partnership: string;
    commercial_case: string;
    bidding_capacity: string;
    other_risks: string;
    conflict_of_interest: string;
    learning_development: string;
    gender_equality_social_inclusion: string;
}

const defaultFormData: BusinessCaseFormData = {
    opportunity_name: '',
    client: '',
    proposal_coordinator: '',
    country_region: '',
    duration_of_project: '',
    budget: '',
    deadline: '',
    workstream: '',
    strategic_case: '',
    competitive_advantage: '',
    corporate_experience: '',
    prospective_team_configuration: '',
    partnership: '',
    commercial_case: '',
    bidding_capacity: '',
    other_risks: '',
    conflict_of_interest: '',
    learning_development: '',
    gender_equality_social_inclusion: ''
};

// Workstream options for dropdown
const WORKSTREAM_OPTIONS = [
    'Climate',
    'Health',
    'MELSS',
    'Migration',
    'Multilaterals',
    'Peacebuilding',
    'MEL Partnerships'
];

const BusinessCaseFormPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [formData, setFormData] = useState<BusinessCaseFormData>(defaultFormData);
    const [attachedFile, setAttachedFile] = useState<File | null>(null);
    const [existingAttachment, setExistingAttachment] = useState<{filename: string; size?: number; data?: string} | null>(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning'>('success');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [editingDraftId, setEditingDraftId] = useState<number | null>(null);

    // Get URL params using React Router
    const editId = searchParams.get('edit');
    const isResubmit = searchParams.get('resubmit') === 'true';
    const loadDraft = searchParams.get('draft') === 'true';

    // Load draft on mount or when editing (only if explicitly requested via URL params)
    useEffect(() => {
        if (editId || loadDraft) {
            loadDraftOrEdit();
        }
    }, [editId, loadDraft]);

    // Auto-save draft every 30 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            saveDraft();
        }, 30000);

        return () => clearInterval(interval);
    }, [formData, attachedFile, editingDraftId, existingAttachment]);

    const loadDraftOrEdit = async () => {
        try {
            const principal = await getClientPrincipal();
            if (!principal?.userDetails) return;

            console.log('URL params - editId:', editId, 'isResubmit:', isResubmit);
            
            if (editId) {
                // Load existing business case for editing
                console.log('=== LOADING BUSINESS CASE BY ID ===');
                const { fetchBusinessCaseById } = await import('../services/businessCase');
                console.log('Calling fetchBusinessCaseById with ID:', editId);
                const businessCase = await fetchBusinessCaseById(parseInt(editId));
                console.log('fetchBusinessCaseById returned:', businessCase);
                
                if (businessCase) {
                    setFormData({
                        opportunity_name: businessCase.opportunity_name || '',
                        client: businessCase.client || '',
                        proposal_coordinator: businessCase.proposal_coordinator || '',
                        country_region: businessCase.country_region || '',
                        duration_of_project: businessCase.duration_of_project || '',
                        budget: businessCase.budget || '',
                        deadline: businessCase.deadline || '',
                        workstream: businessCase.workstream || '',
                        strategic_case: businessCase.strategic_case || '',
                        competitive_advantage: businessCase.competitive_advantage || '',
                        corporate_experience: businessCase.corporate_experience || '',
                        prospective_team_configuration: businessCase.prospective_team_configuration || '',
                        partnership: businessCase.partnership || '',
                        commercial_case: businessCase.commercial_case || '',
                        bidding_capacity: businessCase.bidding_capacity || '',
                        other_risks: businessCase.other_risks || '',
                        conflict_of_interest: businessCase.conflict_of_interest || '',
                        learning_development: businessCase.learning_development || '',
                        gender_equality_social_inclusion: businessCase.gender_equality_social_inclusion || ''
                    });
                    setEditingDraftId(businessCase.id!);
                    
                    // Restore existing attachment info if present
                    if (businessCase.attachment_filename) {
                        setExistingAttachment({
                            filename: businessCase.attachment_filename,
                            size: businessCase.attachment_size,
                            data: businessCase.attachment_data
                        });
                    }
                    
                    // Show appropriate message based on context
                    if (isResubmit && businessCase.status === 'rejected') {
                        setSnackbarMessage('Rejected business case loaded. Make any changes and resubmit.');
                    } else if (businessCase.status === 'draft') {
                        setSnackbarMessage('Draft loaded');
                    } else {
                        setSnackbarMessage('Business case loaded for editing');
                    }
                    setSnackbarSeverity('success');
                    setSnackbarOpen(true);
                }
            } else {
                // Load draft if no edit ID
                const draft = await fetchDraftBusinessCase(principal.userDetails);
                if (draft && draft.id) {
                    setFormData({
                        opportunity_name: draft.opportunity_name || '',
                        client: draft.client || '',
                        proposal_coordinator: draft.proposal_coordinator || '',
                        country_region: draft.country_region || '',
                        duration_of_project: draft.duration_of_project || '',
                        budget: draft.budget || '',
                        deadline: draft.deadline || '',
                        workstream: draft.workstream || '',
                        strategic_case: draft.strategic_case || '',
                        competitive_advantage: draft.competitive_advantage || '',
                        corporate_experience: draft.corporate_experience || '',
                        prospective_team_configuration: draft.prospective_team_configuration || '',
                        partnership: draft.partnership || '',
                        commercial_case: draft.commercial_case || '',
                        bidding_capacity: draft.bidding_capacity || '',
                        other_risks: draft.other_risks || '',
                        conflict_of_interest: draft.conflict_of_interest || '',
                        learning_development: draft.learning_development || '',
                        gender_equality_social_inclusion: draft.gender_equality_social_inclusion || ''
                    });
                    setEditingDraftId(draft.id);
                    
                    // Restore existing attachment info if present
                    if (draft.attachment_filename) {
                        setExistingAttachment({
                            filename: draft.attachment_filename,
                            size: draft.attachment_size,
                            data: draft.attachment_data
                        });
                    }
                    
                    setSnackbarMessage('Draft loaded');
                    setSnackbarSeverity('success');
                    setSnackbarOpen(true);
                }
            }
        } catch (error) {
            console.error('Error loading draft or business case:', error);
        }
    };

    const saveDraft = async () => {
        try {
            const principal = await getClientPrincipal();
            if (!principal?.userDetails || !principal?.name) return;

            // Only save if there's some content
            if (formData.opportunity_name || formData.client) {
                // Include attachment data if file is attached (new or existing)
                let draftData: any = { ...formData };
                console.log('=== SAVING DRAFT ===');
                console.log('attachedFile:', attachedFile);
                console.log('existingAttachment:', existingAttachment?.filename);
                if (attachedFile) {
                    const fileBase64 = await fileToBase64(attachedFile);
                    console.log('fileBase64 length:', fileBase64?.length);
                    draftData.attachment_filename = attachedFile.name;
                    draftData.attachment_size = attachedFile.size;
                    draftData.attachment_data = fileBase64;
                } else if (existingAttachment) {
                    // Preserve existing attachment if no new file attached
                    draftData.attachment_filename = existingAttachment.filename;
                    draftData.attachment_size = existingAttachment.size;
                    draftData.attachment_data = existingAttachment.data;
                }
                console.log('draftData has attachment_data:', !!draftData.attachment_data);
                
                const savedDraft = await saveDraftBusinessCase(principal.userDetails, principal.name, draftData, editingDraftId ?? undefined);
                if (savedDraft && savedDraft.id && !editingDraftId) {
                    // Update editingDraftId if this was a new draft
                    setEditingDraftId(savedDraft.id);
                    console.log('Draft created with ID:', savedDraft.id);
                } else {
                    console.log('Draft auto-saved');
                }
            }
        } catch (error) {
            console.error('Error saving draft:', error);
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            setAttachedFile(event.target.files[0]);
        }
    };

    const handleRemoveFile = () => {
        setAttachedFile(null);
        setExistingAttachment(null);  // Also clear existing attachment when user removes file
    };

    const handleSubmit = async () => {
        // Validate required fields
        if (!formData.opportunity_name.trim()) {
            setSnackbarMessage('Opportunity Name is required');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        setConfirmDialogOpen(true);
    };

    const confirmSubmit = async () => {
        setConfirmDialogOpen(false);
        setIsSubmitting(true);

        try {
            const principal = await getClientPrincipal();
            if (!principal?.userDetails || !principal?.name) {
                throw new Error('User not authenticated');
            }

            // Convert file to base64 if attached (do this first so we can store it)
            let fileBase64 = '';
            if (attachedFile) {
                fileBase64 = await fileToBase64(attachedFile);
            }

            // Prepare business case data with attachment content
            // If new file attached, use it; otherwise preserve existing attachment
            const businessCaseData: BusinessCaseClient = {
                owner_email: principal.userDetails,
                owner_name: principal.name,
                status: 'pending',
                ...formData,
                attachment_filename: attachedFile?.name || existingAttachment?.filename,
                attachment_size: attachedFile?.size || existingAttachment?.size,
                attachment_data: fileBase64 || existingAttachment?.data  // Store base64 in database
            };

            // Submit to database (update if editing/resubmitting, create new otherwise)
            // Use editingDraftId if set, or fall back to editId from URL params
            const idToUpdate = editingDraftId || (editId ? parseInt(editId) : null);
            let savedBusinessCase: BusinessCaseClient;
            if (idToUpdate) {
                const { updateBusinessCase } = await import('../services/businessCase');
                savedBusinessCase = await updateBusinessCase(idToUpdate, businessCaseData);
            } else {
                savedBusinessCase = await submitBusinessCase(businessCaseData);
            }

            // Send email notification
            const emailSent = await sendBusinessCaseSubmission({
                owner_email: principal.userDetails,
                owner_name: principal.name,
                opportunity_name: formData.opportunity_name,
                client: formData.client,
                proposal_coordinator: formData.proposal_coordinator,
                country_region: formData.country_region,
                duration_of_project: formData.duration_of_project,
                budget: formData.budget,
                deadline: formData.deadline,
                attachment_filename: attachedFile?.name || existingAttachment?.filename,
                id: savedBusinessCase.id || 0
            });

            // No need to delete draft - it was updated to 'pending' status above

            if (emailSent) {
                setSnackbarMessage('Business case submitted successfully!');
            } else {
                setSnackbarMessage('Business case submitted, but the notification email could not be sent. Please inform the approvals team manually.');
            }
            setSnackbarSeverity(emailSent ? 'success' : 'warning');
            setSnackbarOpen(true);

            // Reset form
            setFormData(defaultFormData);
            setAttachedFile(null);
            setExistingAttachment(null);

            // Navigate to My Business Cases after 2 seconds
            setTimeout(() => {
                navigate('/my-business-cases');
            }, 2000);

        } catch (error) {
            console.error('Error submitting business case:', error);
            setSnackbarMessage('Failed to submit business case. Please try again.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });
    };

    const handleCancel = () => {
        setFormData(defaultFormData);
        setAttachedFile(null);
        setExistingAttachment(null);
        navigate('/my-business-cases');
    };

    const handleManualSave = async () => {
        try {
            const principal = await getClientPrincipal();
            if (!principal?.userDetails || !principal?.name) {
                setSnackbarMessage('User not authenticated');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                return;
            }

            // Validate that there's some content to save
            if (!formData.opportunity_name && !formData.client) {
                setSnackbarMessage('Please enter at least an Opportunity Name or Client before saving');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                return;
            }

            // Prepare draft data with attachment
            let draftData: any = { ...formData };
            if (attachedFile) {
                const fileBase64 = await fileToBase64(attachedFile);
                draftData.attachment_filename = attachedFile.name;
                draftData.attachment_size = attachedFile.size;
                draftData.attachment_data = fileBase64;
            } else if (existingAttachment) {
                draftData.attachment_filename = existingAttachment.filename;
                draftData.attachment_size = existingAttachment.size;
                draftData.attachment_data = existingAttachment.data;
            }

            // Save the draft (pass editingDraftId to update existing case if editing)
            const savedDraft = await saveDraftBusinessCase(principal.userDetails, principal.name, draftData, editingDraftId ?? undefined);
            
            if (savedDraft && savedDraft.id) {
                setEditingDraftId(savedDraft.id);
                setSnackbarMessage('Draft saved successfully!');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);

                // Navigate to My Business Cases after a short delay
                setTimeout(() => {
                    navigate('/my-business-cases');
                }, 1500);
            } else {
                throw new Error('Failed to save draft');
            }
        } catch (error) {
            console.error('Error saving draft:', error);
            setSnackbarMessage('Failed to save draft. Please try again.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#009688', mb: 3 }}>
                    Business Case
                </Typography>

                <Stack spacing={3}>
                    {/* Header Section - Row 1 */}
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                required
                                label="Opportunity Name"
                                value={formData.opportunity_name}
                                onChange={(e) => setFormData(prev => ({ ...prev, opportunity_name: e.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Client"
                                value={formData.client}
                                onChange={(e) => setFormData(prev => ({ ...prev, client: e.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Proposal Coordinator"
                                value={formData.proposal_coordinator}
                                onChange={(e) => setFormData(prev => ({ ...prev, proposal_coordinator: e.target.value }))}
                            />
                        </Grid>
                    </Grid>

                    {/* Header Section - Row 2 */}
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Country / Region"
                                value={formData.country_region}
                                onChange={(e) => setFormData(prev => ({ ...prev, country_region: e.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Duration of Project"
                                value={formData.duration_of_project}
                                onChange={(e) => setFormData(prev => ({ ...prev, duration_of_project: e.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                label="Budget"
                                value={formData.budget}
                                onChange={(e) => setFormData(prev => ({ ...prev, budget: e.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                label="Deadline"
                                type="date"
                                value={formData.deadline}
                                onChange={(e) => setFormData(prev => ({ ...prev, deadline: e.target.value }))}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button
                                fullWidth
                                variant="outlined"
                                component="label"
                                startIcon={<AttachFileIcon />}
                                sx={{ height: '56px' }}
                            >
                                Attach File
                                <input
                                    type="file"
                                    hidden
                                    onChange={handleFileChange}
                                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                                />
                            </Button>
                            <Typography variant="caption" sx={{ color: '#009688', textAlign: 'center', display: 'block', mt: 0.5 }}>
                                Attach Draft Budget Tracker
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Display attached file (new or existing) */}
                    {(attachedFile || existingAttachment) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                                label={attachedFile?.name || existingAttachment?.filename}
                                onDelete={handleRemoveFile}
                                deleteIcon={<DeleteIcon />}
                                color="primary"
                                variant="outlined"
                            />
                            <Typography variant="caption" color="text.secondary">
                                ({((attachedFile?.size || existingAttachment?.size || 0) / 1024).toFixed(2)} KB)
                            </Typography>
                            {!attachedFile && existingAttachment && (
                                <Typography variant="caption" color="text.secondary">
                                    (saved)
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Workstream Dropdown */}
                    <FormControl fullWidth>
                        <InputLabel id="workstream-label">Workstream</InputLabel>
                        <Select
                            labelId="workstream-label"
                            value={formData.workstream}
                            label="Workstream"
                            onChange={(e) => setFormData(prev => ({ ...prev, workstream: e.target.value }))}
                        >
                            <MenuItem value=""><em>Select workstream</em></MenuItem>
                            {WORKSTREAM_OPTIONS.map((option) => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="h6" sx={{ mt: 2 }}>
                        Please answer the following questions
                    </Typography>

                    {/* Strategic Case */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Strategic Case
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                            {`How does this opportunity fit our current corporate strategy or thematic strategy?
Why is this client important?
Is the opportunity a one-off project or does it have realistic long-term benefits to the company – following on work, excellent project reference etc.`}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.strategic_case}
                            onChange={(e) => setFormData(prev => ({ ...prev, strategic_case: e.target.value }))}
                        />
                    </Box>

                    {/* Competitive Advantage */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Competitive Advantage
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                            {`Why would this client hire IOD PARC – what can we offer that our competitors can't.
Specific corporate knowledge and experience?
Strong and existing client relationship?
The right team?
Other advantages?`}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.competitive_advantage}
                            onChange={(e) => setFormData(prev => ({ ...prev, competitive_advantage: e.target.value }))}
                        />
                    </Box>

                    {/* Corporate Experience */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Corporate Experience
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                            {`Do we have sufficient relevant experience to pursue this opportunity?
Can we fully meet the requirements?`}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.corporate_experience}
                            onChange={(e) => setFormData(prev => ({ ...prev, corporate_experience: e.target.value }))}
                        />
                    </Box>

                    {/* Prospective Team Configuration */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Prospective Team Configuration
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                            {`Please outline the proposed team structure for this bid, clearly identifying the team leader and any other roles already assigned.
Where you have gaps in the team, please liaise with the BD Hub, at the earliest opportunity, to identify and secure the most suitable candidate(s).

Points to consider:
• Do we have the necessary capabilities and resources to pursue?
• Will the bid be delivered using internal resources, external associates, or a combination of both?
• Are we engaging the most appropriate external associates for this work, or defaulting to familiar options?
• Please consider the team's Gender balance and Localisation (are we prioritising local or regional consultants where appropriate?)`}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.prospective_team_configuration}
                            onChange={(e) => setFormData(prev => ({ ...prev, prospective_team_configuration: e.target.value }))}
                        />
                    </Box>

                    {/* Partnership */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Partnership
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                            {`Do we need to identify a Partner, either to lead or sub-contract?
What value add, or gap does the partner fill?
Is the Partner a strategic target as well? i.e. an identified long-term partner?`}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.partnership}
                            onChange={(e) => setFormData(prev => ({ ...prev, partnership: e.target.value }))}
                        />
                    </Box>

                    {/* Commercial Case */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Commercial Case
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                            {`What is the size of the project (can be an estimate) – small / medium / large?
Is the project achievable against the budget?
Are there any currency related risks?
Are the payment terms acceptable?

At this stage, it would be of benefit if you can complete the Workplan and Financial Template, though we recognise if this is an EOI it will not be possible.`}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.commercial_case}
                            onChange={(e) => setFormData(prev => ({ ...prev, commercial_case: e.target.value }))}
                        />
                    </Box>

                    {/* Bidding Capacity */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Bidding Capacity
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                            {`Do you have time and availability to develop a high-quality proposal within the bidding window?
Who else will be involved in the proposal development process?
Who will QA the bid?`}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.bidding_capacity}
                            onChange={(e) => setFormData(prev => ({ ...prev, bidding_capacity: e.target.value }))}
                        />
                    </Box>

                    {/* Other Risks */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Other Risks
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                            {`Are there any other risks you foresee, not associated with or covered by the points above? Consider internal and external
Security risks due to high-risk country visits?
Do we know the client's rules and procedures?`}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.other_risks}
                            onChange={(e) => setFormData(prev => ({ ...prev, other_risks: e.target.value }))}
                        />
                        
                        {/* Conflicts of Interest - Free text field */}
                        <TextField
                            fullWidth
                            label="Any conflicts of interest?"
                            multiline
                            rows={2}
                            value={formData.conflict_of_interest}
                            onChange={(e) => setFormData(prev => ({ ...prev, conflict_of_interest: e.target.value }))}
                            placeholder="Please describe any potential conflicts of interest..."
                            sx={{ mt: 2 }}
                        />
                    </Box>

                    {/* Learning & Development */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Learning & Development
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                            {`Does the opportunity provide any L&D potential for internal staff?
Does it expand our capabilities?`}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.learning_development}
                            onChange={(e) => setFormData(prev => ({ ...prev, learning_development: e.target.value }))}
                        />
                    </Box>

                    {/* Gender Equality and Social Inclusion */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            How will Gender Equality and Social Inclusion be considered in this proposal and project?
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.gender_equality_social_inclusion}
                            onChange={(e) => setFormData(prev => ({ ...prev, gender_equality_social_inclusion: e.target.value }))}
                        />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Action Buttons */}
                    <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 3 }}>
                        <Button
                            variant="outlined"
                            startIcon={<SaveIcon />}
                            onClick={handleManualSave}
                        >
                            Save Draft
                        </Button>
                        
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={handleCancel}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<SendIcon />}
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                sx={{
                                    bgcolor: '#009688',
                                    '&:hover': {
                                        bgcolor: '#00796b'
                                    }
                                }}
                            >
                                {isSubmitting ? 'Submitting...' : 'Submit'}
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </Paper>

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
                <DialogTitle>Confirm Submission</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to submit this business case? It will be sent to business_case_approval@iodparc.com for review.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
                    <Button onClick={confirmSubmit} variant="contained" color="primary">
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default BusinessCaseFormPage;
